import React, { useEffect, useState } from "react"
import { Box, Typography, Container, Paper, Button } from "@mui/material"
import { doc, setDoc, onSnapshot, updateDoc, serverTimestamp } from "firebase/firestore"
import { db } from "../../../lib/firebase"
import RefreshIcon from "@mui/icons-material/Refresh"
import SiteWrapper from "../../../views/home-pages/SiteWrapper"

// Helper to generate 4-letter code
const generateCode = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789" // Removed confusing chars like I, 1, O, 0
  let code = ""
  for (let i = 0; i < 4; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return code
}

const BuzzerHost = () => {
  const [gameCode, setGameCode] = useState("")
  const [sessionData, setSessionData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // 1. Generate Code & Create Session
    const newCode = generateCode()
    setGameCode(newCode)

    const sessionRef = doc(db, "buzzer_sessions", newCode)

    const createSession = async () => {
      // Calculate expiration time (12 hours from now)
      const expireAt = new Date(Date.now() + 12 * 60 * 60 * 1000)
      
      await setDoc(sessionRef, {
        created_at: serverTimestamp(),
        expireAt: expireAt,  // TTL field - Firestore will auto-delete after this time
        host_active: true,
        buzzed_user: null,
        buzzed_at: null,
        players: []
      })
    }

    createSession()

    // 2. Subscribe to Session
    const unsubscribe = onSnapshot(sessionRef, (doc) => {
      if (doc.exists()) {
        setSessionData(doc.data())
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const handleReset = async () => {
    if (!gameCode) return
    const sessionRef = doc(db, "buzzer_sessions", gameCode)
    await updateDoc(sessionRef, {
      buzzed_user: null,
      buzzed_at: null
    })
  }

  // Game show buzzer sound effect using Web Audio API
  const playBuzzerSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      
      // Create a "ding" sound - game show style
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      // High-pitched "ding" frequency
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime) // A5 note
      oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.1) // Slide up
      
      // Quick attack, medium decay
      gainNode.gain.setValueAtTime(0.5, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4)
      
      oscillator.type = 'sine'
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.4)
    } catch (e) {
    }
  }

  // Play sound when someone buzzes in
  const prevBuzzedUser = React.useRef(null)
  React.useEffect(() => {
    // If we went from no buzz to a buzz, play sound
    if (sessionData?.buzzed_user && !prevBuzzedUser.current) {
      playBuzzerSound()
    }
    prevBuzzedUser.current = sessionData?.buzzed_user || null
  }, [sessionData?.buzzed_user])

  if (loading) {
    return (
      <SiteWrapper>
        <Container sx={{ mt: 8, textAlign: "center" }}>
          <Typography variant="h4" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>Creating Session...</Typography>
        </Container>
      </SiteWrapper>
    )
  }

  // Helper for consistent colors
  const getColorFromName = (name) => {
    const colors = [
      "#FF5733", "#33FF57", "#3357FF", "#FF33A1", "#FFD700", 
      "#00FFFF", "#FF8C00", "#8A2BE2", "#00FA9A", "#FF1493"
    ]
    let hash = 0
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }
    const index = Math.abs(hash % colors.length)
    return colors[index]
  }

  const isBuzzed = !!sessionData?.buzzed_user

  return (
    <SiteWrapper>
      <Container maxWidth="lg" sx={{ mt: 4, height: "80vh", display: "flex", flexDirection: "column", position: "relative", zIndex: 2 }}>
        {/* Header with Code */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Typography variant="h5" sx={{ color: "rgba(255,255,255,0.6)" }}>
            JOIN CODE:
          </Typography>
          <Paper sx={{ px: 4, py: 1, display: "flex", alignItems: "center", gap: 2, bgcolor: "rgba(245, 158, 11, 0.08)", color: "#fbbf24", border: "1px solid rgba(245, 158, 11, 0.2)", borderRadius: "12px" }}>
            <Typography variant="h2" sx={{ fontWeight: "bold", letterSpacing: 6 }}>
              {gameCode}
            </Typography>
          </Paper>
          <Box>
             <Typography variant="subtitle1" sx={{ color: "rgba(255,255,255,0.7)" }}>
               Players: {sessionData?.players?.length || 0}
             </Typography>
          </Box>
        </Box>

      {/* Main Buzz Area */}
      <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
        {isBuzzed ? (
          <Box sx={{ textAlign: "center", animation: "popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)" }}>
             <Typography variant="h1" sx={{ 
               fontSize: "6rem", 
               fontWeight: "bold", 
               color: getColorFromName(sessionData.buzzed_user), // Use player's color
               textShadow: "4px 4px 0px rgba(0,0,0,0.1)"
             }}>
               {sessionData.buzzed_user}
             </Typography>
             <Typography variant="h4" color="text.secondary" sx={{ mt: 2 }}>
               BUZZED IN!
             </Typography>
          </Box>
        ) : (
          <Typography variant="h2" sx={{ fontWeight: "light", mb: 4, color: "rgba(255,255,255,0.25)" }}>
            WAITING FOR BUZZ...
          </Typography>
        )}
      </Box>

      {/* Player Grid */}
      <Box sx={{ 
        display: "flex", 
        flexWrap: "wrap", 
        gap: 2, 
        justifyContent: "center", 
        mb: 4,
        maxHeight: "30vh",
        overflowY: "auto",
        p: 2
      }}>
        {sessionData?.players?.map((player) => (
          <Paper 
            key={player} 
            elevation={3}
            sx={{ 
              px: 3, 
              py: 2, 
              bgcolor: getColorFromName(player),
              color: "white",
              fontWeight: "bold",
              fontSize: "1.2rem",
              borderRadius: "16px",
              opacity: isBuzzed && sessionData.buzzed_user !== player ? 0.3 : 1, // Dim others if someone buzzed
              transform: isBuzzed && sessionData.buzzed_user === player ? "scale(1.2)" : "scale(1)",
              transition: "all 0.3s"
            }}
          >
            {player}
          </Paper>
        ))}
      </Box>

      {/* Controls */}
      <Box sx={{ textAlign: "center", mb: 8 }}>
        <Button 
          variant="contained" 
          size="large" 
          onClick={handleReset}
          startIcon={<RefreshIcon />}
          sx={{ 
            fontSize: "1.5rem", 
            px: 6, 
            py: 2, 
            borderRadius: 4,
            bgcolor: isBuzzed ? "#ef4444" : "rgba(255, 255, 255, 0.05)",
            color: isBuzzed ? "white" : "rgba(255, 255, 255, 0.3)",
            "&:hover": {
              bgcolor: isBuzzed ? "#dc2626" : "rgba(255, 255, 255, 0.05)"
            }
          }}
          disabled={!isBuzzed}
        >
          RESET BUZZER
        </Button>
      </Box>
      
      <style>
        {`
        @keyframes popIn {
          from { opacity: 0; transform: scale(0.5); }
          to { opacity: 1; transform: scale(1); }
        }
        `}
      </style>
    </Container>
    </SiteWrapper>
  )
}

export default BuzzerHost
